import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { EVENT, PARTNERS } from "@/data/siteData";
import { ArrowUpRight, Volume2, VolumeX } from "lucide-react";

export const Hero = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    const next = !muted;
    el.muted = next;
    if (!next) {
      // When unmuting, ensure playback resumes
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
    setMuted(next);
  };

  return (
    <section id="home" data-testid="hero-section" className="relative border-b border-zinc-200 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-12 md:pb-16">
        {/* Top meta strip */}
        <div className="flex items-center justify-between overline text-zinc-500 mb-8 md:mb-10">
          <span data-testid="hero-edition">{EVENT.edition} / INVITATION-ONLY</span>
          <span className="hidden sm:inline">— THE BACKBONE OF TECHNOLOGY —</span>
          <span className="hidden md:inline">LONDON — DUBAI — BENGALURU — SINGAPORE</span>
        </div>

        <div data-testid="hero-initiative" className="flex items-center justify-center gap-3 mb-6 md:mb-8">
          <span className="font-mono text-xs md:text-sm uppercase tracking-[0.3em] text-zinc-500">A</span>
          <img
            src="/pierce-smith-logo.webp"
            alt="Pierce & Smith"
            data-testid="hero-initiative-logo"
            className="h-14 md:h-20 w-auto mix-blend-multiply"
          />
          <span className="font-mono text-xs md:text-sm uppercase tracking-[0.3em] text-zinc-500">Initiative</span>
        </div>

        <div data-testid="hero-powered-by" className="flex items-center justify-center gap-2 -mt-3 md:-mt-4 mb-6 md:mb-8">
          <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-zinc-500">Powered by</span>
          <a
            href="https://www.whitepageinternational.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-powered-by-wpi-link"
            className="transition-opacity hover:opacity-80"
          >
            <img src="/wpi-logo.jpeg" alt="White Page International" className="h-14 md:h-16 w-auto" />
          </a>
        </div>

        {/* Upcoming banner — centred, sits high above the grid */}
        <div
          data-testid="hero-upcoming-banner"
          className="mb-10 md:mb-12 flex justify-center"
        >
          <div className="inline-flex bg-[#FF2A00] text-white">
            <a
              href="#registration"
              data-testid="hero-banner-india"
              className="block px-5 md:px-7 py-3 md:py-4 text-center border-r border-white/30 hover:bg-black transition-colors"
            >
              <div className="font-display text-[10px] md:text-xs tracking-[0.28em] uppercase text-white/85">
                Upcoming
              </div>
              <div className="font-display text-lg md:text-2xl tracking-[0.15em] mt-1">
                BoT <span className="uppercase">INDIA</span>
              </div>
              <div className="overline text-[10px] md:text-[11px] tracking-[0.3em] uppercase mt-1 text-white/85">
                Bengaluru
              </div>
              <div data-testid="hero-venue-india" className="font-body text-[10px] md:text-xs mt-1 text-white/70">
                Grand Hyatt Centric
              </div>
              <div data-testid="hero-date-india" className="font-display text-xs md:text-sm uppercase tracking-wide mt-1 text-white">
                Jan 2027
              </div>
            </a>
            <Link
              to="/international-pay"
              data-testid="hero-banner-global"
              className="block px-5 md:px-7 py-3 md:py-4 text-center hover:bg-black transition-colors"
            >
              <div className="font-display text-[10px] md:text-xs tracking-[0.28em] uppercase text-white/85">
                Upcoming
              </div>
              <div className="font-display text-lg md:text-2xl tracking-[0.15em] mt-1">
                BoT <span className="uppercase">GLOBAL</span>
              </div>
              <div className="overline text-[10px] md:text-[11px] tracking-[0.3em] uppercase mt-1 text-white/85">
                London
              </div>
              <div data-testid="hero-venue-global" className="font-body text-[10px] md:text-xs mt-1 text-white/70">
                Excel London
              </div>
              <div data-testid="hero-date-global" className="font-display text-xs md:text-sm uppercase tracking-wide mt-1 text-white">
                March 2027
              </div>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          {/* Left: Headline */}
          <div className="col-span-12 lg:col-span-7">
            <div className="overline text-[#FF2A00] mb-6">◉ Live Registration Open</div>
            <h1 className="font-display text-[13vw] sm:text-[10vw] lg:text-[8.5vw] leading-[0.9] tracking-tighter uppercase">
              <span className="normal-case">BoT</span>
              <br />
              <span className="text-[#002FA7]">Summit</span>
              <br />
              2026
            </h1>
            <p className="mt-8 max-w-xl text-lg text-zinc-700 leading-relaxed">
              Two Days. Ten+ keynotes. Five+ Panel Discussions. The minds building
              the infrastructure, intelligence and institutions of the next internet
              — in one room.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#registration"
                data-testid="hero-register-btn"
                className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-7 py-4 text-xs font-bold tracking-[0.2em] uppercase hover:bg-black transition-colors"
              >
                Secure Your Seat <ArrowUpRight size={16} />
              </a>
              <a
                href="#agenda"
                data-testid="hero-agenda-btn"
                className="inline-flex items-center gap-2 bg-white text-black border border-black px-7 py-4 text-xs font-bold tracking-[0.2em] uppercase hover:bg-black hover:text-white transition-colors"
              >
                View Agenda
              </a>
            </div>
          </div>

          {/* Right: Date + Image card */}
          <div className="col-span-12 lg:col-span-5">
            <div className="relative aspect-[4/5] w-full border border-zinc-200 overflow-hidden bg-zinc-100">
              <video
                ref={videoRef}
                src="https://customer-assets.emergentagent.com/job_bot-summit-2026/artifacts/g8fn6apn_videoplayback.mp4"
                autoPlay
                loop
                muted={muted}
                playsInline
                preload="auto"
                data-testid="hero-video"
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={toggleMute}
                data-testid="hero-video-mute-toggle"
                aria-label={muted ? "Unmute hero video" : "Mute hero video"}
                className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white/90 text-black hover:bg-[#FF2A00] hover:text-white transition-colors backdrop-blur-sm"
              >
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="overline text-white/80">
                  BoT Summit 2026
                </div>
              </div>
              {/* Index marker */}
              <div className="absolute top-4 left-4 bg-white text-black px-2 py-1 overline text-[10px]">
                / 01 HERO
              </div>
            </div>

            {/* Facts stack */}
            <div className="mt-4 grid grid-cols-3 border border-zinc-200">
              <div className="p-4 border-r border-zinc-200">
                <div className="font-display text-3xl">02</div>
                <div className="overline text-zinc-500 mt-1">Sessions</div>
              </div>
              <div className="p-4 border-r border-zinc-200">
                <div className="font-display text-3xl">10+</div>
                <div className="overline text-zinc-500 mt-1">Speakers</div>
              </div>
              <div className="p-4">
                <div className="font-display text-3xl">500+</div>
                <div className="overline text-zinc-500 mt-1">Delegates</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Partner ticker */}
      <div className="border-t border-zinc-200 bg-white overflow-hidden">
        <div className="flex whitespace-nowrap animate-ticker py-4">
          {[...PARTNERS, ...PARTNERS].map((p, i) => (
            <span
              key={i}
              className="mx-10 overline text-zinc-500 flex items-center gap-10"
            >
              {p}
              <span className="w-1 h-1 bg-zinc-400 rounded-full" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
